import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getAlerts, scanNow, fmtPrice } from "@/lib/api";
import { Bell, TrendingUp, TrendingDown, Zap, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const kindLabel = { breakout: "BREAKOUT", breakdown: "BREAKDOWN", volume: "VOLUME", rsi: "RSI", momentum: "MOMENTUM" };

export const AlertsFeed = ({ onSelect, compact = false }) => {
  const { data = [], refetch } = useQuery({ queryKey: ["alerts"], queryFn: () => getAlerts(compact ? 12 : 60), refetchInterval: 20000 });

  const doScan = async () => {
    const res = await scanNow();
    toast.success(`Scan complete · ${res.new_alerts} new signal${res.new_alerts === 1 ? "" : "s"}`);
    refetch();
  };

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden flex flex-col" data-testid="alerts-feed">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-app/60">
        <div className="flex items-center gap-2">
          <Bell size={15} className="text-warn" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">Signal Alerts</h3>
        </div>
        <button data-testid="manual-scan-btn" onClick={doScan} className="flex items-center gap-1.5 text-[10px] font-mono text-ai hover:text-white uppercase transition-colors">
          <RefreshCw size={12} /> Scan now
        </button>
      </div>
      <div className={`overflow-y-auto ${compact ? "max-h-[420px]" : "max-h-[70vh]"}`}>
        {!data.length && (
          <div className="text-center py-10 text-tertiary font-mono text-xs">No signals yet. Alerts appear as the market moves.</div>
        )}
        {data.map((a) => {
          const bull = a.direction === "bullish";
          return (
            <div key={a.id} data-testid={`alert-${a.id}`} onClick={() => onSelect(a.symbol)}
              className="px-4 py-3 border-b border-border-subtle hover:bg-surface-2 cursor-pointer transition-colors fade-up">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-white">{a.symbol}</span>
                  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${bull ? "bg-bull/10 text-bull border-bull/25" : "bg-bear/10 text-bear border-bear/25"}`}>
                    {kindLabel[a.kind] || a.kind}
                  </span>
                </div>
                <span className={`font-mono text-xs font-bold flex items-center gap-1 ${a.suggestion === "CALL" ? "text-bull" : "text-bear"}`}>
                  {a.suggestion === "CALL" ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{a.suggestion}
                </span>
              </div>
              <p className="text-xs text-secondary leading-snug">{a.message}</p>
              <div className="flex items-center gap-3 mt-1.5 text-[10px] font-mono text-tertiary">
                <span>${fmtPrice(a.price)}</span>
                <span className={a.change_pct >= 0 ? "text-bull" : "text-bear"}>{a.change_pct >= 0 ? "+" : ""}{a.change_pct}%</span>
                <span>RSI {a.rsi}</span>
                <span>{a.rel_volume}x vol</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
